import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Gauge, Clock, AlertCircle, PieChart, CheckSquare, Calendar,
  AlertTriangle, BarChart3, Users, Bell, MessageSquare, MapPin,
  Plus, Folder, Star, GripVertical, ImageIcon, Type, Heading1,
  MousePointer, ClipboardList, CalendarDays, ClockIcon, CalendarCheck, Palmtree,
  FileText, Megaphone, Pin, Cloud, Building2, Link, HardHat, Workflow, Sparkles
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Tile metadata with icons
const TILE_META = {
  'compliance-overview': { title: 'Compliance Overview', icon: Gauge, defaultSize: 'medium' },
  'expiring-soon': { title: 'Expiring Soon', icon: Clock, defaultSize: 'medium' },
  'expired-requirements': { title: 'Expired', icon: AlertCircle, defaultSize: 'small' },
  'requirements-status': { title: 'Requirements Status', icon: PieChart, defaultSize: 'large' },
  'training-matrix-mini': { title: 'Training Matrix Mini', icon: Gauge, defaultSize: 'large' },
  'my-tasks': { title: 'My Tasks', icon: CheckSquare, defaultSize: 'medium' },
  'tasks-due-soon': { title: 'Due This Week', icon: Calendar, defaultSize: 'medium' },
  'overdue-tasks': { title: 'Overdue', icon: AlertTriangle, defaultSize: 'small' },
  'task-progress': { title: 'Task Progress', icon: BarChart3, defaultSize: 'medium' },
  'team-overview': { title: 'Team', icon: Users, defaultSize: 'small' },
  'team-directory': { title: 'Team Directory', icon: Users, defaultSize: 'medium' },
  'team-calendar': { title: 'Team Calendar', icon: CalendarDays, defaultSize: 'large' },
  'pending-leave': { title: 'Pending Leave Requests', icon: Clock, defaultSize: 'medium' },
  'upcoming-leave': { title: 'Upcoming Leave', icon: CalendarCheck, defaultSize: 'medium' },
  'leave-balance': { title: 'Leave Balance', icon: Palmtree, defaultSize: 'small' },
  'notifications': { title: 'Notifications', icon: Bell, defaultSize: 'medium' },
  'unread-messages': { title: 'Messages', icon: MessageSquare, defaultSize: 'small' },
  'recent-posts': { title: 'Recent Posts', icon: FileText, defaultSize: 'medium' },
  'announcements': { title: 'Announcements', icon: Megaphone, defaultSize: 'medium' },
  'quick-checkin': { title: 'Check-In', icon: MapPin, defaultSize: 'small' },
  'create-task': { title: 'Quick Task', icon: Plus, defaultSize: 'small' },
  'my-spaces': { title: 'My Spaces', icon: Folder, defaultSize: 'large' },
  'favorite-spaces': { title: 'Favorites', icon: Star, defaultSize: 'medium' },
  'image': { title: 'Image', icon: ImageIcon, defaultSize: 'medium' },
  'text': { title: 'Rich Text', icon: Type, defaultSize: 'medium' },
  'title': { title: 'Title', icon: Heading1, defaultSize: 'small' },
  // New tiles
  'bookings-overview': { title: 'Bookings', icon: Calendar, defaultSize: 'medium', isNew: true },
  'checklists-due': { title: 'Checklists Due', icon: ClipboardList, defaultSize: 'medium', isNew: true },
  'site-checkins-today': { title: 'Site Check-ins', icon: MapPin, defaultSize: 'medium', isNew: true },
  'training-quotes': { title: 'Training Quotes', icon: FileText, defaultSize: 'medium', isNew: true },
  'weather-widget': { title: 'Weather', icon: Cloud, defaultSize: 'small', isNew: true },
  'company-stats': { title: 'Company Stats', icon: Building2, defaultSize: 'large', isNew: true },
  'quick-links': { title: 'Quick Links', icon: Link, defaultSize: 'medium', isNew: true },
  'ppe-status': { title: 'PPE Status', icon: HardHat, defaultSize: 'medium', isNew: true },
  'documents-recent': { title: 'Recent Documents', icon: FileText, defaultSize: 'medium', isNew: true },
  'workflows-active': { title: 'Active Workflows', icon: Workflow, defaultSize: 'medium', isNew: true },
  // Action buttons
  'btn-create-task': { title: 'Create Task Button', icon: CheckSquare, defaultSize: 'small' },
  'btn-checkin': { title: 'Check-In Button', icon: MapPin, defaultSize: 'small' },
  'btn-message': { title: 'New Message Button', icon: MessageSquare, defaultSize: 'small' },
  'btn-booking': { title: 'New Booking Button', icon: Calendar, defaultSize: 'small' },
  'btn-team': { title: 'View Team Button', icon: Users, defaultSize: 'small' },
  'btn-checklists': { title: 'Checklists Button', icon: ClipboardList, defaultSize: 'small' },
};

// Tile categories
const TILE_CATEGORIES = [
  {
    id: 'new',
    name: 'New',
    tiles: ['bookings-overview', 'checklists-due', 'site-checkins-today', 'training-quotes', 'weather-widget', 'company-stats', 'quick-links', 'ppe-status', 'documents-recent', 'workflows-active']
  },
  {
    id: 'compliance',
    name: 'Compliance',
    tiles: ['compliance-overview', 'expiring-soon', 'expired-requirements', 'requirements-status', 'training-matrix-mini']
  },
  {
    id: 'tasks',
    name: 'Tasks',
    tiles: ['my-tasks', 'tasks-due-soon', 'overdue-tasks', 'task-progress']
  },
  {
    id: 'team',
    name: 'Team',
    tiles: ['team-overview', 'team-directory', 'company-stats']
  },
  {
    id: 'attendance',
    name: 'Attendance',
    tiles: ['team-calendar', 'pending-leave', 'upcoming-leave', 'leave-balance', 'site-checkins-today']
  },
  {
    id: 'communication',
    name: 'Communication',
    tiles: ['notifications', 'unread-messages', 'recent-posts', 'announcements']
  },
  {
    id: 'training',
    name: 'Training',
    tiles: ['bookings-overview', 'training-quotes']
  },
  {
    id: 'operations',
    name: 'Operations',
    tiles: ['checklists-due', 'ppe-status', 'workflows-active', 'documents-recent']
  },
  {
    id: 'actions',
    name: 'Quick Actions',
    tiles: ['quick-checkin', 'create-task', 'quick-links']
  },
  {
    id: 'spaces',
    name: 'Spaces',
    tiles: ['my-spaces', 'favorite-spaces']
  },
  {
    id: 'widgets',
    name: 'Widgets',
    tiles: ['weather-widget', 'quick-links']
  },
  {
    id: 'design',
    name: 'Design',
    tiles: ['title', 'text', 'image']
  },
  {
    id: 'buttons',
    name: 'Buttons',
    tiles: ['btn-create-task', 'btn-checkin', 'btn-message', 'btn-booking', 'btn-team', 'btn-checklists']
  }
];

// Draggable tile card component
function DraggableTileCard({ tileType, count = 0, onAdd }) {
  const meta = TILE_META[tileType];
  const Icon = meta?.icon || Folder;
  const isNew = meta?.isNew;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: `sidebar-${tileType}`,
    data: {
      tileType,
      fromSidebar: true,
    },
  });

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-3 rounded-lg border bg-white transition-all",
        isDragging && "opacity-50 shadow-lg z-50",
        "hover:border-gray-300 hover:shadow-sm",
        isNew && "ring-1 ring-violet-200 bg-violet-50/30"
      )}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Icon */}
      <div className={cn(
        "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
        isNew ? "bg-violet-100" : "bg-gray-100"
      )}>
        <Icon className={cn("h-4 w-4", isNew ? "text-violet-600" : "text-gray-600")} />
      </div>

      {/* Title and info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium truncate">{meta?.title || tileType}</p>
          {isNew && (
            <Badge className="bg-violet-500 text-white text-[9px] px-1.5 py-0 h-4 font-semibold">
              NEW
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 capitalize">{meta?.defaultSize}</p>
      </div>

      {/* Count badge + Add button */}
      <div className="flex items-center gap-1.5 shrink-0">
        {count > 0 && (
          <Badge variant="secondary" className="bg-gray-100 text-gray-600 h-6 min-w-6 flex items-center justify-center">
            {count}
          </Badge>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onAdd(tileType)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function TilePaletteSidebar({ open, onClose, activeTileTypes = [], onAddTile }) {
  // Count occurrences of each tile type
  const typeCounts = activeTileTypes.reduce((acc, type) => {
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Get total count for a category
  const getCategoryCount = (tiles) => {
    return tiles.reduce((sum, t) => sum + (typeCounts[t] || 0), 0);
  };

  return (
    <div
      className={cn(
        "fixed top-0 right-0 h-full w-80 bg-white border-l shadow-xl z-30",
        "flex flex-col transition-transform duration-300 ease-in-out",
        open ? "translate-x-0" : "translate-x-full"
      )}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b shrink-0">
        <h2 className="text-lg font-semibold">Add Tiles</h2>
        <p className="text-sm text-gray-500">
          Drag tiles to the dashboard or click + to add
        </p>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="p-4">
          <Accordion type="multiple" defaultValue={TILE_CATEGORIES.map(c => c.id)} className="space-y-2">
            {TILE_CATEGORIES.map((category) => {
              const categoryCount = getCategoryCount(category.tiles);
              return (
                <AccordionItem key={category.id} value={category.id} className="border rounded-lg">
                  <AccordionTrigger className="px-3 py-2 hover:no-underline">
                    <span className="text-sm font-medium">{category.name}</span>
                    {categoryCount > 0 && (
                      <Badge variant="secondary" className="ml-2 bg-gray-100">
                        {categoryCount}
                      </Badge>
                    )}
                  </AccordionTrigger>
                  <AccordionContent className="px-2 pb-2">
                    <div className="space-y-2">
                      {category.tiles.map((tileType) => (
                        <DraggableTileCard
                          key={tileType}
                          tileType={tileType}
                          count={typeCounts[tileType] || 0}
                          onAdd={onAddTile}
                        />
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-3 bg-gray-50 shrink-0">
        <p className="text-xs text-gray-500 text-center">
          {activeTileTypes.length} tile{activeTileTypes.length !== 1 ? 's' : ''} on dashboard
        </p>
      </div>
    </div>
  );
}

// Export tile meta for use in other components
export { TILE_META, TILE_CATEGORIES };
